<script lang="ts" setup>
interface UITableColumn<T extends Record<string, any> = Record<string, any>> {
    label: string;
    key: keyof T;
    sortable?: boolean;
    direction?: "asc" | "desc";
}

interface UITableSort {
    key: string;
    direction: "asc" | "desc";
}

interface UITableProps<T extends Record<string, any> = Record<string, any>> {
    sort?: UITableSort;
    selected?: T[];
    rows: T[];
    columns?: UITableColumn<T>[];
}

const props = defineProps<UITableProps>();

const emit = defineEmits<{
    (e: "update:selected", v: UITableProps["selected"]): void;
    (e: "update:sort", v: UITableProps["sort"]): void;
}>();

const innerSelected = computed({
    get: () => props.selected,
    set: (modelValue) => {
        emit("update:selected", modelValue);
    },
});

const columnMap = computed(() => {
    let columns = props.columns ?? [];

    if (!columns.length && props.rows[0]) {
        columns = Object.keys(props.rows[0]).map((key) => ({
            key,
            label: key,
        }));
    }

    return columns.map((column) => {
        if (column.sortable && props.sort?.key === column.key)
            column.direction = props.sort?.direction;
        else column.direction = undefined;
        return column;
    });
});

function updateSort(sort: UITableSort | undefined) {
    emit("update:sort", sort);
}
</script>

<template>
    <UTable
        v-model="innerSelected"
        :rows="rows"
        :columns="columnMap"
        :ui="{
            wrapper: 'relative overflow-visible',
            checkbox: {
                padding: 'ps-8',
            },
            th: {
                padding: 'px-4 first:pl-8 last:pr-8 py-3.5',
            },
            td: {
                padding: 'px-4 first:pl-8 last:pr-8 py-4',
            },
        }"
    >
        <template
            v-for="column in columnMap"
            :key="`${column.key}${column.direction ?? ''}`"
            #[`${column.key}-header`]
        >
            <UITableHead :column="column" @sort="updateSort" />
        </template>
        <template v-for="(index, name) in $slots" :key="name" #[name]="data">
            <slot :name="name" v-bind="data"></slot>
        </template>
        <template v-if="$slots.empty" #empty-state>
            <slot name="empty" />
        </template>
        <template #loading-state>
            <slot name="loading">
                <div class="flex items-center justify-center h-full w-full flex-col gap-4 py-20">
                    <HdLoader loading />
                </div>
            </slot>
        </template>
    </UTable>
</template>